import { log } from "utils/log";

import { fetchTileSetFile } from "./tiledSource";
import { type TiledDrawingSource, tileRelativePath } from "./tileMath";

const l = log.miniMap.tiled;

/** Concurrent tile download cap — a fast pan must not spike memory on mobile. */
const MAX_CONCURRENT_TILE_FETCHES = 8;

/**
 * How many times one tile may fail before it is treated as permanently
 * broken. A genuinely bad tile must stop costing requests; a tile that hit a
 * blip must not stay a hole for the whole session.
 */
const MAX_TILE_LOAD_ATTEMPTS = 3;
/** Backoff before the next attempt, indexed by failures so far. */
const TILE_RETRY_BACKOFF_MS = [1_000, 5_000];
/**
 * Added to the scheduled repaint's delay so it lands *after* `retryAfter`
 * rather than racing it. The deadline is measured with `performance.now()`
 * but waited on with `setTimeout`, and two clocks arriving at the same
 * instant would let `mayAttempt` reject the very repaint that exists to
 * trigger the retry.
 */
const TILE_RETRY_TIMER_SLACK_MS = 50;

let activeFetches = 0;
/**
 * Waiting requests, most-recently-queued last. Released LIFO (as a stack, via
 * `pop`), not FIFO: every draw pass only requests tiles inside the *current*
 * viewport (`drawTiledImage.ts`), so the most recently queued request is
 * whatever the user is looking at right now. A FIFO queue would instead serve
 * requests in original request order — after a zoom/pan, that means draining
 * a backlog from the *previous* viewport (or the initial full-image fit view)
 * before ever reaching the new one, which is the delay this fixes.
 */
const fetchQueue: (() => void)[] = [];

function acquireFetchSlot(): Promise<void> {
    if (activeFetches < MAX_CONCURRENT_TILE_FETCHES) {
        activeFetches++;
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        fetchQueue.push(() => {
            activeFetches++;
            resolve();
        });
    });
}

function releaseFetchSlot(): void {
    activeFetches--;
    const next = fetchQueue.pop();
    if (next) {
        next();
    }
}

/**
 * LRU of decoded tile bitmaps for one drawing. Resident memory is bounded by
 * `maxTiles * ~256 KB` regardless of document size; evicted bitmaps are
 * closed immediately (the compressed originals stay in the HTTP cache).
 * `getOrRequest` is synchronous: a draw pass paints what's cached and
 * schedules the rest; `onRepaintNeeded` asks the owner to repaint, both per
 * tile arrival and once a failed tile's backoff has elapsed (see
 * {@link scheduleRetry}).
 *
 * `beginFrame` + the per-key generation in {@link getOrRequest} let a load
 * that finishes after the viewport has moved on drop itself instead of
 * entering the LRU: the LIFO fetch queue alone only reorders *which pending
 * fetch starts next*, it doesn't stop an off-screen tile that already
 * started from completing and evicting a just-loaded current-viewport tile
 * (see BM-13597 code review). Callers that never call `beginFrame` (e.g.
 * tests driving `getOrRequest` directly) get the old behavior: every tile
 * is always considered wanted.
 */
export class TileBitmapCache {
    private tiles = new Map<string, ImageBitmap>(); // Insertion order = LRU order (oldest first).
    private pending = new Set<string>();
    /**
     * Failure bookkeeping per tile, so a transient error (a network blip, a
     * signature the refresh couldn't recover) isn't a permanent hole for the
     * session the way a plain "give up once" set would make it.
     */
    private failures = new Map<string, { attempts: number; retryAfter: number }>();
    /** Pending "the backoff has elapsed, repaint" callbacks, keyed by tile. */
    private retryTimers = new Map<string, ReturnType<typeof setTimeout>>();
    private disposed = false;
    private currentGeneration = 0;
    private lastWantedGeneration = new Map<string, number>();

    constructor(
        private source: TiledDrawingSource,
        private options: { maxTiles: number; onRepaintNeeded: () => void },
    ) {}

    /** Mark the start of a new paint pass: tiles not re-requested before the next call become stale. */
    beginFrame(): void {
        this.currentGeneration++;
    }

    /** Resident bitmap for the tile (refreshes recency), else schedules a download and returns undefined. */
    getOrRequest(level: number, x: number, y: number): ImageBitmap | undefined {
        const key = `${level}/${x}/${y}`;
        this.lastWantedGeneration.set(key, this.currentGeneration);
        const hit = this.tiles.get(key);
        if (hit) {
            this.tiles.delete(key);
            this.tiles.set(key, hit);
            return hit;
        }
        if (!this.pending.has(key) && this.mayAttempt(key)) {
            this.pending.add(key);
            void this.load(key, level, x, y);
        }
        return undefined;
    }

    /** Close every resident bitmap, reject late downloads, and drop pending retries. */
    dispose(): void {
        this.disposed = true;
        for (const bitmap of this.tiles.values()) {
            bitmap.close();
        }
        for (const timer of this.retryTimers.values()) {
            clearTimeout(timer);
        }
        this.retryTimers.clear();
        this.tiles.clear();
        this.pending.clear();
        this.failures.clear();
    }

    /** Whether this tile is worth another request: never failed, or out of backoff with attempts left. */
    private mayAttempt(key: string): boolean {
        const failure = this.failures.get(key);
        return !failure || (failure.attempts < MAX_TILE_LOAD_ATTEMPTS && performance.now() >= failure.retryAfter);
    }

    private recordFailure(key: string): void {
        const attempts = (this.failures.get(key)?.attempts ?? 0) + 1;
        // `?? 0` for the final attempt: it exhausts `MAX_TILE_LOAD_ATTEMPTS`,
        // so `mayAttempt` rejects it regardless of when it would be eligible.
        const backoff = TILE_RETRY_BACKOFF_MS[attempts - 1] ?? 0;
        this.failures.set(key, { attempts, retryAfter: performance.now() + backoff });
        if (attempts < MAX_TILE_LOAD_ATTEMPTS) {
            this.scheduleRetry(key, backoff);
        }
    }

    /**
     * Ask the owner to repaint once this tile's backoff has elapsed, so the
     * retry actually happens.
     *
     * A retry only occurs when a paint calls {@link getOrRequest} again, and a
     * failure produces no paint on its own: `onRepaintNeeded` fires on
     * success, and the large map repaints on that or a user gesture. Siblings
     * succeeding in the same burst don't help either — their repaint lands
     * inside this backoff, so `mayAttempt` refuses. Without this timer a
     * stationary viewport keeps its hole until the user pans, which is the
     * same "permanent until you do something" failure the one-shot blacklist
     * had (PR #1698 review).
     *
     * One timer per failed load, never per paint. Many tiles failing at once
     * (an offline burst) therefore schedule many timers, but the owner
     * coalesces their repaints into one animation frame.
     */
    private scheduleRetry(key: string, backoff: number): void {
        this.cancelRetry(key);
        this.retryTimers.set(
            key,
            setTimeout(() => {
                this.retryTimers.delete(key);
                if (!this.disposed) {
                    this.options.onRepaintNeeded();
                }
            }, backoff + TILE_RETRY_TIMER_SLACK_MS),
        );
    }

    private cancelRetry(key: string): void {
        const timer = this.retryTimers.get(key);
        if (timer !== undefined) {
            clearTimeout(timer);
            this.retryTimers.delete(key);
        }
    }

    /** Whether `key` was requested in the most recent {@link beginFrame} pass (always true if `beginFrame` is unused). */
    private isStillWanted(key: string): boolean {
        return this.lastWantedGeneration.get(key) === this.currentGeneration;
    }

    private async load(key: string, level: number, x: number, y: number): Promise<void> {
        await acquireFetchSlot();
        try {
            if (this.disposed || !this.isStillWanted(key)) {
                // Viewport moved on before this tile's turn came up -- drop it
                // rather than spending a network fetch on stale work.
                return;
            }
            const relativePath = tileRelativePath(this.source, level, x, y);
            const response = await fetchTileSetFile(this.source, relativePath);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status} for tile ${relativePath}`);
            }
            const blob = await response.blob();
            const bitmap = await createImageBitmap(blob);
            if (this.disposed || !this.isStillWanted(key)) {
                // Viewport moved on while this fetch/decode was in flight --
                // discard instead of inserting, so it can't evict a
                // current-viewport tile from the LRU (BM-13597 code review).
                bitmap.close();
                return;
            }
            this.tiles.set(key, bitmap);
            this.failures.delete(key);
            this.cancelRetry(key);
            while (this.tiles.size > this.options.maxTiles) {
                const oldestKey = this.tiles.keys().next().value as string;
                const oldest = this.tiles.get(oldestKey);
                this.tiles.delete(oldestKey);
                oldest?.close();
            }
            this.options.onRepaintNeeded();
        } catch (error) {
            this.recordFailure(key);
            l.warn("Failed to load drawing tile:", key, error);
        } finally {
            this.pending.delete(key);
            releaseFetchSlot();
        }
    }
}
